import { useState, useEffect, useRef, useCallback } from "react";
import Move from './Move';
import { last } from './arrayHelpers';

const MAX_DELTA_MS = 3000;
const SUBMIT_PAUSE_MS = 300;
const INTER_MOVE_PAUSE_MS = 500;

export function findFirstActive(solved, n) {
  for (let i = 0; i < n; i++) {
    if (!solved.has(i)) return i;
  }
  return n;
}

// Reference keystroke decoder: turns an emitted keystroke code back into a
// (word, cursor) edit, mirroring the server's replay reconstruction. Exported so
// the keystroke-replay test can use it as an oracle against KeyBoard's encoder.
export function applyKeystroke(eventCode, word, cursor, solved, n) {
  word = [...word];

  const nextActiveCursor = (from) => {
    let c = from + 1;
    while (c < n && solved.has(c)) c++;
    return c;
  };

  const prevActiveCursor = (from) => {
    let c = from - 1;
    while (c >= 0 && solved.has(c)) c--;
    return c < 0 ? from : c;
  };

  if (eventCode >= 1 && eventCode <= 26) {
    const letter = String.fromCharCode(96 + eventCode);
    if (cursor < n && !solved.has(cursor)) {
      word[cursor] = letter;
      cursor = nextActiveCursor(cursor);
    }
  } else if (eventCode === 27) {
    if (cursor < n && !solved.has(cursor)) {
      word[cursor] = '.';
      cursor = nextActiveCursor(cursor);
    }
  } else if (eventCode === 28) {
    if (cursor < n && !solved.has(cursor)) {
      word[cursor] = ' ';
    }
  } else if (eventCode === 29) {
    const prev = prevActiveCursor(cursor);
    if (prev < cursor) {
      word[prev] = ' ';
      cursor = prev;
    }
  } else if (eventCode === 30) {
    for (let i = 0; i < n; i++) {
      if (!solved.has(i)) word[i] = ' ';
    }
    cursor = findFirstActive(solved, n);
  } else if (eventCode === 32) {
    if (cursor < n && !solved.has(cursor)) {
      word[cursor] = ' ';
      cursor = nextActiveCursor(cursor);
    }
  } else if (eventCode >= 100) {
    cursor = eventCode - 100;
  }

  return [word, cursor];
}

function buildTimeline(validMoves, n) {
  const events = [];

  for (let mi = 0; mi < validMoves.length; mi++) {
    const move = validMoves[mi];
    const prevMove = mi > 0 ? validMoves[mi - 1] : null;

    events.push({ type: 'init_move', delay: mi === 0 ? 0 : INTER_MOVE_PAUSE_MS, move: move });

    if (move.keystrokes && move.keystrokes.length > 0) {
      for (let ki = 0; ki < move.keystrokes.length; ki++) {
        const ks = move.keystrokes[ki];
        let delay;
        if (ki === 0) {
          if (mi === 0) {
            delay = 0;
          } else {
            delay = Math.min(ks.elapsed_ms - prevMove.elapsed_ms, MAX_DELTA_MS);
          }
        } else {
          delay = Math.min(ks.elapsed_ms - move.keystrokes[ki - 1].elapsed_ms, MAX_DELTA_MS);
        }
        events.push({ type: 'keystroke', event: ks.event, delay: Math.max(delay, 0) });
      }
    }

    const lastKsElapsed = (move.keystrokes && move.keystrokes.length > 0)
      ? move.keystrokes[move.keystrokes.length - 1].elapsed_ms
      : (prevMove ? prevMove.elapsed_ms : move.elapsed_ms);
    const submitDelay = Math.max(Math.min(move.elapsed_ms - lastKsElapsed, MAX_DELTA_MS), SUBMIT_PAUSE_MS);
    events.push({ type: 'submit', move: move, delay: submitDelay });
  }

  return events;
}

/**
 * Pre-compute every snapshot along the timeline so we can seek to any point instantly.
 * Returns an array of { word, cursor, completedMoves, solved, moveSolved } — one per event index (plus index 0 = initial state).
 */
function buildSnapshots(timeline, validMoves, n) {
  const snapshots = [];

  let word = Array(n).fill(' ');
  let cursor = 0;
  let solved = new Map();
  let moveSolved = new Map();
  let completed = [];

  // Snapshot 0: initial blank state
  snapshots.push({
    word: [...word],
    cursor,
    completedMoves: [...completed],
    solved: new Map(solved),
    moveSolved: new Map(moveSolved),
    currentWord: null,  // nothing being typed yet
    moveIndex: 0,       // which move we're on
  });

  const initMoveWord = (move) => {
    const w = Array(n).fill(' ');
    const currentSolved = new Map(solved);

    if (move && move.optimized_pos) {
      try {
        const optPos = JSON.parse(move.optimized_pos);
        optPos.forEach((choices, pos) => {
          if (choices.length === 1) {
            currentSolved.set(pos, choices[0]);
          }
        });
      } catch (e) {
        console.error("Failed to parse optimized_pos in replay", e);
      }
    }

    currentSolved.forEach((letter, pos) => { w[pos] = letter; });
    const c = findFirstActive(currentSolved, n);
    word = w;
    cursor = c;
    moveSolved = currentSolved;
  };

  for (let ei = 0; ei < timeline.length; ei++) {
    const ev = timeline[ei];

    if (ev.type === 'init_move') {
      initMoveWord(ev.move);
      snapshots.push({
        word: [...word],
        cursor,
        completedMoves: [...completed],
        solved: new Map(solved),
        moveSolved: new Map(moveSolved),
        currentWord: [...word],
        moveIndex: completed.length,
      });
    } else if (ev.type === 'keystroke') {
      const [w, c] = applyKeystroke(ev.event, word, cursor, moveSolved, n);
      word = w;
      cursor = c;
      snapshots.push({
        word: [...word],
        cursor,
        completedMoves: [...completed],
        solved: new Map(solved),
        moveSolved: new Map(moveSolved),
        currentWord: [...word],
        moveIndex: completed.length,
      });
    } else if (ev.type === 'submit') {
      completed = [...completed, ev.move];
      for (let j = 0; j < n; j++) {
        if (ev.move.reply[j] === 'G') {
          solved.set(j, ev.move.guess[j]);
        }
      }
      snapshots.push({
        word: null,
        cursor: 0,
        completedMoves: [...completed],
        solved: new Map(solved),
        moveSolved: new Map(moveSolved),
        currentWord: null,
        moveIndex: completed.length,
      });
    }
  }

  return snapshots;
}

function GameReplay({ game, vsize, gameMaxScore, allGamesMaxAvg, gameAvgScore, gameMmScore, allGamesAvg, allGamesMmAvg, onReplayEnd }) {
  const n = game.n;
  const validMoves = game.game_moves.filter(m => m.reply !== "Z".repeat(n));

  // Build timeline and snapshots once
  const timelineRef = useRef(null);
  const snapshotsRef = useRef(null);
  if (!timelineRef.current) {
    timelineRef.current = buildTimeline(validMoves, n);
    snapshotsRef.current = buildSnapshots(timelineRef.current, validMoves, n);
  }
  const timeline = timelineRef.current;
  const snapshots = snapshotsRef.current;
  const maxIndex = snapshots.length - 1;

  // Cumulative time for the slider (so position maps to "real time")
  const cumulativeTimeRef = useRef(null);
  if (!cumulativeTimeRef.current) {
    const ct = [0]; // snapshot 0 is at t=0
    for (let i = 0; i < timeline.length; i++) {
      ct.push(ct[i] + timeline[i].delay);
    }
    cumulativeTimeRef.current = ct;
  }
  const cumulativeTime = cumulativeTimeRef.current;
  const totalDuration = cumulativeTime[cumulativeTime.length - 1];

  const [eventIndex, setEventIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  // Derive display state from the current snapshot
  const snap = snapshots[eventIndex] || snapshots[0];
  const completedMoves = snap.completedMoves;
  const currentWord = snap.currentWord;
  const currentCursor = snap.cursor;

  // Advance playback by one event
  const advanceOne = useCallback(() => {
    setEventIndex(prev => {
      const next = prev + 1;
      if (next > maxIndex) {
        setPlaying(false);
        if (onReplayEnd) setTimeout(onReplayEnd, 500);
        return prev;
      }
      return next;
    });
  }, [maxIndex, onReplayEnd]);

  // Playback engine: schedule next event based on its delay
  useEffect(() => {
    if (!playing) return;
    if (eventIndex >= maxIndex) {
      setPlaying(false);
      return;
    }
    // The delay for the NEXT event (timeline[eventIndex] corresponds to snapshots[eventIndex+1])
    const nextDelay = timeline[eventIndex] ? timeline[eventIndex].delay : 0;
    timerRef.current = setTimeout(advanceOne, nextDelay);
    return () => clearTimeout(timerRef.current);
  }, [playing, eventIndex, maxIndex, timeline, advanceOne]);

  // Seek handler
  const handleSeek = (e) => {
    const target = parseInt(e.target.value, 10);
    setEventIndex(target);
    // Don't auto-resume on seek — let the user control
  };

  // Play / Pause toggle
  const togglePlay = () => {
    if (eventIndex >= maxIndex) {
      // restart from beginning
      setEventIndex(0);
      setPlaying(true);
    } else {
      setPlaying(p => !p);
    }
  };

  // Rewind
  const rewind = () => {
    setEventIndex(0);
    setPlaying(false);
  };

  // Step backward
  const stepBack = () => {
    setPlaying(false);
    setEventIndex(prev => Math.max(0, prev - 1));
  };

  // Step forward
  const stepForward = () => {
    setPlaying(false);
    setEventIndex(prev => Math.min(maxIndex, prev + 1));
  };

  // Format time as m:ss
  const formatTime = (ms) => {
    const s = Math.round(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Compute move markers for the timeline — positions where submits happen
  const moveMarkers = [];
  for (let i = 0; i < snapshots.length; i++) {
    if (i > 0 && timeline[i - 1]?.type === 'submit') {
      moveMarkers.push(i);
    }
  }

  // Compute move boundaries — snapshot indices where each move's init_move lives
  const moveBoundaries = [0]; // start of replay
  for (let i = 0; i < timeline.length; i++) {
    if (timeline[i].type === 'init_move') {
      moveBoundaries.push(i + 1); // snapshots are offset by 1 from timeline
    }
  }

  // Jump to previous move boundary
  const prevMoveJump = () => {
    setPlaying(false);
    setEventIndex(prev => {
      // Find the last boundary strictly before current position
      for (let i = moveBoundaries.length - 1; i >= 0; i--) {
        if (moveBoundaries[i] < prev) return moveBoundaries[i];
      }
      return 0;
    });
  };

  // Jump to next move boundary
  const nextMoveJump = () => {
    setPlaying(false);
    setEventIndex(prev => {
      // Find the first boundary strictly after current position
      for (let i = 0; i < moveBoundaries.length; i++) {
        if (moveBoundaries[i] > prev) return moveBoundaries[i];
      }
      return maxIndex;
    });
  };

  // Progress percentage for the filled portion of the slider
  const progressPct = maxIndex > 0 ? (eventIndex / maxIndex) * 100 : 0;

  return (
    <>
      {/* Timeline controls */}
      <div className="replay-controls">
        <div className="replay-buttons">
          <button className="replay-btn" onClick={rewind} title="Rewind to start">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
          </button>
          <button className="replay-btn replay-btn-move" onClick={prevMoveJump} title="Previous move">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z"/></svg>
          </button>
          <button className="replay-btn" onClick={stepBack} title="Step back">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
          </button>
          <button className="replay-btn replay-btn-play" onClick={togglePlay} title={playing ? "Pause" : "Play"}>
            {playing ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            ) : eventIndex >= maxIndex ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            )}
          </button>
          <button className="replay-btn" onClick={stepForward} title="Step forward">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
          </button>
          <button className="replay-btn replay-btn-move" onClick={nextMoveJump} title="Next move">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5.5 18l8.5-6-8.5-6v12zm8.5-6l8.5-6v12l-8.5-6z"/></svg>
          </button>
        </div>
        <div className="replay-timeline">
          <span className="replay-time">{formatTime(cumulativeTime[eventIndex])}</span>
          <div className="replay-slider-container">
            <div className="replay-slider-track">
              <div className="replay-slider-fill" style={{ width: `${progressPct}%` }} />
              {moveMarkers.map((mi, idx) => (
                <div
                  key={idx}
                  className="replay-marker"
                  style={{ left: `${(mi / maxIndex) * 100}%` }}
                  title={`Move ${idx + 1}`}
                />
              ))}
            </div>
            <input
              type="range"
              className="replay-slider"
              min={0}
              max={maxIndex}
              value={eventIndex}
              onChange={handleSeek}
            />
          </div>
          <span className="replay-time">{formatTime(totalDuration)}</span>
        </div>
        <div className="replay-move-counter">
          {completedMoves.length}/{validMoves.length} moves
        </div>
      </div>

      {/* Move display */}
      {[...validMoves].reverse().map((move, i) => {
        const moveIndex = validMoves.length - i - 1; // 0 to N-1

        if (moveIndex < completedMoves.length) {
          const cmove = completedMoves[moveIndex];
          return (
            <Move
              i={validMoves.length - i - 1}
              last={i === 0}
              n={n}
              first_pos={[...cmove.guess.matchAll("[a-z]")][0].index}
              last_pos={last([...cmove.guess.matchAll("[a-z]")]).index}
              key={"replay_" + moveIndex}
              guess={cmove.guess}
              reply={cmove.reply}
              seq={validMoves[validMoves.length - i - 0] === undefined ? cmove.seq : (cmove.seq + 1 == validMoves[validMoves.length - i - 0].seq ? "." : cmove.seq)}
              rep={cmove.rep}
              vsize={i === 0 && completedMoves.length === validMoves.length ? vsize : -1}
              invalid={false}
              _show_invalids={() => {}}
              is_complete={() => completedMoves.length === validMoves.length}
              sharing={false}
              onShare={() => {}}
              line_over_first={false}
              clue={cmove.wflr_clue ? cmove.wflr_clue.clue : ""}
              clue_shown={cmove.wflr_clue ? cmove.wflr_clue.clue_shown : false}
              clue_requested={cmove.wflr_clue ? cmove.wflr_clue.clue_requested : false}
              word_rank={cmove.word_rank || 0}
              word_count={cmove.word_count || 0}
              vocab_actual={cmove.vocab_actual || 0}
              vocab_best={cmove.vocab_best || 0}
              vocab_worst={cmove.vocab_worst || 0}
              mm_actual={cmove.mm_actual || 0}
              mm_best={cmove.mm_best || 0}
              mm_worst={cmove.mm_worst || 0}
              gameMaxScore={gameMaxScore}
              allGamesMaxAvg={allGamesMaxAvg}
              gameAvgScore={gameAvgScore}
              gameMmScore={gameMmScore}
              allGamesAvg={allGamesAvg}
              allGamesMmAvg={allGamesMmAvg}
            />
          );
        } else if (moveIndex === completedMoves.length && currentWord) {
          return (
            <div key="current_word" className="a" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
              {currentWord.map((ch, j) => (
                <div key={j} className={"b" + (n > 7 ? " shrink" : "") + (j === currentCursor && j < n ? " c" : "")}>
                  {ch}
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <div key={"hidden_" + moveIndex} style={{ visibility: "hidden" }}>
              <Move
                i={validMoves.length - i - 1}
                last={i === 0}
                n={n}
                first_pos={[...move.guess.matchAll("[a-z]")][0].index}
                last_pos={last([...move.guess.matchAll("[a-z]")]).index}
                key={"replay_hidden_" + moveIndex}
                guess={move.guess}
                reply={move.reply}
                seq={validMoves[validMoves.length - i - 0] === undefined ? move.seq : (move.seq + 1 == validMoves[validMoves.length - i - 0].seq ? "." : move.seq)}
                rep={move.rep}
                vsize={-1}
                invalid={false}
                _show_invalids={() => {}}
                is_complete={() => false}
                sharing={false}
                onShare={() => {}}
                line_over_first={false}
                clue={""}
                clue_shown={false}
                clue_requested={false}
                word_rank={0}
                word_count={0}
                vocab_actual={0}
                vocab_best={0}
                vocab_worst={0}
                mm_actual={0}
                mm_best={0}
                mm_worst={0}
                gameMaxScore={null}
                allGamesMaxAvg={null}
              />
            </div>
          );
        }
      })}
    </>
  );
}

export default GameReplay;
